/**
 * @module app/routes
 * @description Application route declarations enforcing distinct auth & routing boundaries:
 * - Client portal: /login and /client/* protected routes (e.g., /client/display-module)
 * - Super Admin portal: /super-admin/login and /s-admin/* protected application routes
 */

import * as React from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useAuth, LoginPage } from "@/features/auth";
import { ClientLoginPage } from "@/features/client";
import { UsersPage } from "@/features/users";
import { ClientsPage, ClientFormPage } from "@/features/clients";
import {
  ClientDeviceClientsPage,
  OrgDeviceManagementPage,
} from "@/features/client-device-management";
import { DisplayModulePage } from "@/features/display-module";
import { DevicesPage, DeviceTelemetryPage } from "@/features/devices";
import { ProfilePage } from "@/features/profile";
import { TermsPage, PrivacyPolicyPage } from "@/features/legal";
import { NotFoundPage } from "@/features/public";
import { AppShell } from "@/components/layout";
import { AppLoader } from "@/components/ui/app-loader";

/**
 * Guard that enforces authenticated session for Super Admin routes (`/s-admin/*`).
 * Redirects unauthenticated attempts to `/super-admin/login`.
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoader message="Loading..." submessage="" />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/super-admin/login" state={{ from: location }} replace />
    );
  }

  if (user?.role === "client") {
    return <Navigate to="/client/display-module" replace />;
  }

  return (
    <AppShell currentUser={user} onLogout={logout}>
      <Outlet />
    </AppShell>
  );
}

/**
 * Guard that enforces authenticated session for Client Portal routes (`/client/*`).
 * Redirects unauthenticated attempts to `/login`.
 */
function ClientProtectedRoute() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoader message="Loading client workspace..." submessage="" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <AppShell currentUser={user} onLogout={logout}>
      <Outlet />
    </AppShell>
  );
}

/**
 * Guard for public authentication pages that redirects already-authenticated users to their portal.
 */
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <AppLoader message="Connecting to platform gateway..." />;
  }

  if (isAuthenticated) {
    const dest =
      user?.role === "client" ? "/client/display-module" : "/s-admin/users";
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
}

/**
 * Root path redirect: sends authenticated users to their portal, otherwise lands on `/login` (Client portal).
 */
function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <AppLoader message="Loading platform gateway..." />;
  }

  if (isAuthenticated) {
    const dest =
      user?.role === "client" ? "/client/display-module" : "/s-admin/users";
    return <Navigate to={dest} replace />;
  }

  return <Navigate to="/login" replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Client Portal authentication (/login) */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <ClientLoginPage />
          </PublicOnlyRoute>
        }
      />

      {/* Client Portal protected routes under /client/ prefix */}
      <Route path="/client" element={<ClientProtectedRoute />}>
        <Route index element={<Navigate to="/client/display-module" replace />} />
        <Route path="display-module" element={<DisplayModulePage />} />
      </Route>

      {/* Super Admin authentication (/super-admin/login) */}
      <Route
        path="/super-admin/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      {/* Convenient alias for super-admin login */}
      <Route
        path="/s-admin/login"
        element={<Navigate to="/super-admin/login" replace />}
      />
      <Route
        path="/super-admin"
        element={<Navigate to="/super-admin/login" replace />}
      />

      {/* Super Admin protected routes under /s-admin/ prefix */}
      <Route path="/s-admin" element={<ProtectedRoute />}>
        <Route index element={<Navigate to="/s-admin/users" replace />} />
        <Route
          path="dashboard"
          element={<Navigate to="/s-admin/users" replace />}
        />
        <Route path="users" element={<UsersPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/onboard" element={<ClientFormPage mode="create" />} />
        <Route
          path="clients/:clientId/edit"
          element={<ClientFormPage mode="edit" />}
        />
        <Route path="client-devices" element={<ClientDeviceClientsPage />} />
        <Route
          path="client-devices/:clientId"
          element={<OrgDeviceManagementPage />}
        />
        <Route path="display-module" element={<DisplayModulePage />} />
        <Route path="devices" element={<DevicesPage />} />
        <Route
          path="devices/:deviceId/telemetry"
          element={<DeviceTelemetryPage />}
        />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Legal & Compliance routes */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route
        path="/terms-and-conditions"
        element={<Navigate to="/terms" replace />}
      />
      <Route
        path="/privacy-policy"
        element={<Navigate to="/privacy" replace />}
      />

      {/* 404 Page (Direct route + Catch-all) */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
